import React, { useEffect, useState, useRef, useCallback } from 'react';
import FilterDropdown from './FilterDropdown';
import ActionButtons from './ActionButtons';
import { useQueryBuilder } from '../context/QueryBuilderContext';
import { FILTER_OPERATORS } from '../../../utils/constants';

interface AssetTypeFilterDropdownProps {
  closeDropdown: () => void;
  fetchSearchAssetTypes: (payload: any, headers: any, options?: any) => Promise<any>;
}

const LIMIT = 20;
const DEBOUNCE_DELAY = 500;

const AssetTypeFilterDropdown: React.FC<AssetTypeFilterDropdownProps> = ({ closeDropdown, fetchSearchAssetTypes }) => {
  const { queryBuilder, setCondition, removeCondition, getConditionValue } = useQueryBuilder();
  const [assetTypes, setAssetTypes] = useState<any[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const loadingRef = useRef(false);

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, DEBOUNCE_DELAY);
    return () => clearTimeout(timeout);
  }, [searchText]);

  const fetchPage = useCallback(async (skipVal: number, searchVal: string) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);

    const payload: any = {
      object_type: 'assetType',
      search_text: searchVal,
      search_field: 'title',
      limit: LIMIT,
      skip: skipVal,
      fields: ['uid', 'title'],
    };

    try {
      const { results = [], count = 0 } = await fetchSearchAssetTypes(payload, {});

      setAssetTypes(prev => skipVal === 0 ? results : [...prev, ...results]);
      setHasMore(skipVal + results.length < count);
    } catch (err) {
      console.error('Failed to fetch asset types:', err);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [fetchSearchAssetTypes]);

  useEffect(() => {
    setSkip(0);
    setAssetTypes([]);
    setHasMore(true);
    fetchPage(0, debouncedSearchText);
  }, [debouncedSearchText]);

  useEffect(() => {
    if (skip > 0) {
      fetchPage(skip, debouncedSearchText);
    }
  }, [skip]);

  const handleScroll = () => {
    if (!dropdownRef.current || loadingRef.current || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = dropdownRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      setSkip((prev) => prev + LIMIT);
    }
  };

  const clearAll = () => {
    setSelectedItems([]);
  };

  const applyFilter = () => {
    if (selectedItems.length > 0) {
      setCondition('asset_type_uid', FILTER_OPERATORS.IN, selectedItems);
    } else {
      removeCondition('asset_type_uid');
    }
    closeDropdown();
  };

  useEffect(() => {
    const existing = getConditionValue('asset_type_uid');
    if (existing?.[FILTER_OPERATORS.IN]) {
      setSelectedItems(existing[FILTER_OPERATORS.IN]);
    }
  }, [queryBuilder]);

  return (
    <div>
      <input
        type="text"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        placeholder={'Start typing to see results...'}
        style={{ width: '100%', padding: '8px', marginBottom: '8px', boxSizing: 'border-box' }}
      />

      <div
        ref={dropdownRef}
        onScroll={handleScroll}
        style={{ maxHeight: '300px', minHeight: '80px', overflowY: 'auto', boxSizing: 'border-box' }}
      >
        {!loading && assetTypes.length === 0 ? (
          <div style={{
            color: 'var(--color-font-tertiary, #6C7C8C)',
            fontSize: '14px',
            padding: '16px 12px 20px',
            textAlign: 'center',
          }}>
            No results found
          </div>
        ) : (
          <FilterDropdown
            data={assetTypes.map((item) => ({ name: item.title, id: item.uid }))}
            field="title"
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            loading={loading}
          />
        )}
      </div>

      <ActionButtons
        closeDropdown={closeDropdown}
        clearAll={clearAll}
        applyFilter={applyFilter}
      />
    </div>
  );
};

export default AssetTypeFilterDropdown;
